/**
 * AggregateRating Schema Component
 * Provides structured rating data for Google Rich Results stars
 * Consolidates ratings from multiple platforms (Google, EnergySage, SolarReviews)
 */

import { useEffect } from "react";

interface AggregateRatingSchemaProps {
  ratingValue?: number;
  reviewCount?: number;
  bestRating?: number;
  worstRating?: number;
  itemName?: string;
  itemType?: "LocalBusiness" | "Product" | "Service" | "Organization";
}

const AggregateRatingSchema = ({
  ratingValue = 4.9,
  reviewCount = 127,
  bestRating = 5,
  worstRating = 1,
  itemName = "California Energy Group",
  itemType = "LocalBusiness"
}: AggregateRatingSchemaProps) => {

  useEffect(() => {
    const ratingSchema = {
      "@context": "https://schema.org",
      "@type": itemType,
      "@id": `https://www.gosolar.pro/#${itemType.toLowerCase()}`,
      "name": itemName,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": ratingValue.toString(),
        "reviewCount": reviewCount.toString(),
        "bestRating": bestRating.toString(),
        "worstRating": worstRating.toString(),
        "ratingExplanation": "Average rating from Google Business, EnergySage, SolarReviews, and BBB"
      }
    };

    const scriptId = `aggregate-rating-schema-${itemType}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(ratingSchema);

    return () => {
      const toRemove = document.getElementById(scriptId);
      if (toRemove) toRemove.remove();
    };
  }, [ratingValue, reviewCount, bestRating, worstRating, itemName, itemType]);

  return null;
};

// Company-wide aggregate rating
export const CompanyRatingSchema = () => (
  <AggregateRatingSchema
    ratingValue={4.9}
    reviewCount={127}
    itemName="California Energy Group"
    itemType="LocalBusiness"
  />
);

// Solar installation service rating
export const SolarServiceRatingSchema = () => (
  <AggregateRatingSchema
    ratingValue={4.9}
    reviewCount={89}
    itemName="Residential Solar + Battery Installation"
    itemType="Service"
  />
);

// PPA product rating
export const PPARatingSchema = () => (
  <AggregateRatingSchema
    ratingValue={4.8}
    reviewCount={72}
    itemName="$ Zero Down Solar PPA"
    itemType="Product"
  />
);

export default AggregateRatingSchema;
